import React from 'react';
import styled from 'styled-components';
import { formatBytes } from '../../../common/utils';
import { PX_TO_REM, SIZES } from '../../../fundamentals/sizes/sizes';
import { IGutterProps, useGutterCss } from '../../../fundamentals/spaces/space';
import { FONT_SIZES, FONT_WEIGHTS } from '../../../fundamentals/typography/typography';
import { useTestId } from '../../../hooks/useTestId/useTestId';
import { Icon } from '../../Icon/Icon';
import { MATERIAL_ICONS, MATERIAL_ICONS_TYPES } from '../../Icon/icons';
import { LinkButton } from '../../LinkButton/LinkButton';
import { Span } from '../../Texts/Texts';
import { BaseDiv } from '../../../common/BaseElements';

export interface IFileWidgetProps extends IGutterProps {
  name?: string;
  size?: number;
  onFileDeleted?: (fileName?: string) => void;
  onFileClicked?: (fileName?: string) => void;
}

const FileWidgetContainer = styled(BaseDiv)<IFileWidgetProps>`
  display: flex;
  width: 100%;
  justify-content: start;
`;

const VerticalContainer = styled(BaseDiv)<IFileWidgetProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
  ${useGutterCss({ left: 12, right: 12 })};
`;

const FileWidgetLink = styled(LinkButton)`
  line-height: ${PX_TO_REM[20]};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

export const FileWidget: React.FC<IFileWidgetProps> = ({ name, size = 0, onFileDeleted, onFileClicked, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'FileWidget');

  return (
    <FileWidgetContainer {...generateTestId()} {...rest}>
      <Icon {...generateTestId('Icon')} name={MATERIAL_ICONS.Description} type={MATERIAL_ICONS_TYPES.OUTLINE} size={SIZES.MICRO}></Icon>
      <VerticalContainer>
        {onFileClicked && (
          <FileWidgetLink constantWidth {...generateTestId('Link')} onClick={() => onFileClicked?.(name)} size={SIZES.SMALL}>
            {name}
          </FileWidgetLink>
        )}

        {!onFileClicked && (
          <Span {...generateTestId('Name')} textSize={FONT_SIZES.TEXT_SMALL}>
            {name}
          </Span>
        )}
        <Span {...generateTestId('Size')} textSize={FONT_SIZES.TEXT_MICRO}>
          {formatBytes(size)}
        </Span>
      </VerticalContainer>
      {onFileDeleted && (
        <Icon
          {...generateTestId('Delete')}
          name={MATERIAL_ICONS.Delete}
          type={MATERIAL_ICONS_TYPES.OUTLINE}
          size={SIZES.MICRO}
          onClick={() => onFileDeleted(name)}
        ></Icon>
      )}
    </FileWidgetContainer>
  );
};
